// Quality Signals — Bank health. The check-back view for the owner ruling
// (review 5w6g1l): the writer AGENT (option B) and the platform's
// update-on-ingest (option A) side by side — how the write stage decided,
// the platform's own dedup verdicts and whether they executed, and what the
// consumer re-pointed. Backend: app/routes/signals_bank.py.

import { useCallback, useEffect, useState } from 'react';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import {
  signalsBankApi,
  type BankHealth,
  type DedupEventRow,
  type SyncResult,
  type WriterRunRow,
} from '../services/signalsBank';
import styles from './SignalsBankPage.module.css';

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function counts(map: Record<string, number> | undefined): string {
  const entries = Object.entries(map ?? {});
  if (entries.length === 0) return 'none';
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k} ${v}`)
    .join(' · ');
}

function flagClass(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return styles.unknown;
  return value ? styles.on : styles.off;
}

function flagLabel(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return 'unknown';
  return value ? 'on' : 'off';
}

export function SignalsBankPage() {
  const [health, setHealth] = useState<BankHealth | null>(null);
  const [events, setEvents] = useState<DedupEventRow[]>([]);
  const [runs, setRuns] = useState<WriterRunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<SyncResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, e, r] = await Promise.all([
        signalsBankApi.health(),
        signalsBankApi.events(50),
        signalsBankApi.writerRuns(14, 50),
      ]);
      setHealth(h);
      setEvents(e.items);
      setRuns(r.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bank health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSync = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const result = await signalsBankApi.sync();
      setSyncNote(result);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={styles.page}>
      <SignalsTabs />
      <div className={styles.header}>
        <h1 className={styles.title}>Bank health</h1>
        <button
          type="button"
          className={styles.button}
          onClick={() => void onSync()}
          disabled={syncing}
          data-testid="sync-now"
        >
          {syncing ? 'Syncing…' : 'Sync platform events now'}
        </button>
      </div>
      <p className={styles.sub}>
        The Quality Signals bank's two anti-duplicate mechanisms, side by side: the writer
        agent that decides create / update / reference for every ingested signal, and the
        platform's own update-on-ingest verdicts. This is the view for the scheduled
        check-back.
      </p>

      {error && <div className={styles.error} role="alert">{error}</div>}
      {loading && !health && <div className={styles.empty}>Loading…</div>}

      {health && (
        <div className={styles.grid}>
          <div className={styles.card}>
            <p className={styles.cardTitle}>Switches</p>
            <div className={styles.chips}>
              <span className={`${styles.chip} ${flagClass(health.flags.agent_writer)}`} data-testid="flag-agent-writer">
                writer agent {flagLabel(health.flags.agent_writer)}
              </span>
              <span className={`${styles.chip} ${flagClass(health.allow_updates)}`} data-testid="flag-allow-updates">
                platform update-on-ingest {flagLabel(health.allow_updates)}
              </span>
              <span className={`${styles.chip} ${flagClass(health.flags.dedup_event_sync)}`}>
                event sync cron {flagLabel(health.flags.dedup_event_sync)}
              </span>
            </div>
            <div className={styles.muted} style={{ marginTop: 8 }}>
              writer model {health.flags.agent_writer_model}
              {health.allow_updates_error ? ` · flag unreadable: ${health.allow_updates_error}` : ''}
            </div>
          </div>

          <div className={styles.card}>
            <p className={styles.cardTitle}>Writer decisions · last {health.writer.window_days}d</p>
            <div className={styles.big} data-testid="writer-runs">{health.writer.runs}</div>
            <div className={styles.muted}>by decision: {counts(health.writer.by_decision)}</div>
            <div className={styles.muted}>by path: {counts(health.writer.by_mode)}</div>
            <div className={styles.muted}>
              {health.writer.with_warnings} with validation warnings · ${health.writer.cost_usd.toFixed(2)}
            </div>
            {Object.keys(health.writer.fallback_reasons).length > 0 && (
              <div className={styles.warn}>fallbacks: {counts(health.writer.fallback_reasons)}</div>
            )}
          </div>

          <div className={styles.card}>
            <p className={styles.cardTitle}>Wasted spend · last {health.writer.window_days}d</p>
            <div className={styles.big} data-testid="double-coverage">
              {health.writer.double_coverage_runs}
              <span className={styles.muted}> / {health.writer.no_change_runs} no-change runs</span>
            </div>
            <div className={styles.muted}>
              runs where another writer already had the story: ${health.writer.double_coverage_cost_usd.toFixed(2)} of
              agent spend + {health.writer.double_coverage_pipeline_tokens.toLocaleString()} pipeline tokens
            </div>
            <div className={styles.muted}>all no-change runs: ${health.writer.no_change_cost_usd.toFixed(2)}</div>
            <div className={styles.muted}>matched entries by writer: {counts(health.writer.by_matched_writer)}</div>
          </div>

          <div className={styles.card}>
            <p className={styles.cardTitle}>Platform dedup events</p>
            <div className={styles.big} data-testid="events-total">{health.events.total}</div>
            <div className={styles.muted}>verdicts: {counts(health.events.by_action)}</div>
            <div className={styles.muted}>execution: {counts(health.events.by_execution_status)}</div>
            <div className={styles.muted}>consumer: {counts(health.events.by_consume_state)}</div>
            <div className={styles.muted}>
              {health.events.citations_rewritten} citations + {health.events.ingestion_rows_repointed} ingestion rows re-pointed
            </div>
            <div className={styles.muted}>
              last event {fmt(health.events.last_event_at)} · last sync {fmt(health.events.last_synced_at)}
            </div>
          </div>
        </div>
      )}

      {syncNote && (
        <div className={styles.note} data-testid="sync-note">
          Sync: {syncNote.listed} listed, {syncNote.recorded} new, {syncNote.applied} applied,{' '}
          {syncNote.no_action} no action, {syncNote.failed} failed
          {syncNote.error ? ` · ${syncNote.error}` : ''}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.h2}>Recent writer runs</h2>
        </div>
        {runs.length === 0 ? (
          <div className={styles.empty}>No write-stage runs recorded in the window.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table} data-testid="writer-runs-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Email</th>
                  <th>Path</th>
                  <th>Decision</th>
                  <th>Entry</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.record_id}>
                    <td className={styles.muted}>{fmt(r.processed_at)}</td>
                    <td>
                      <div>{r.email_subject ?? '(no subject)'}</div>
                      <div className={`${styles.muted} ${styles.mono}`}>{r.mailbox} · {r.email_id.slice(0, 8)}</div>
                    </td>
                    <td>
                      <span className={styles.chip}>{r.mode ?? '—'}</span>
                      {r.fallback_reason && <div className={styles.warn}>{r.fallback_reason}</div>}
                    </td>
                    <td>
                      <span className={styles.chip}>{r.decision ?? r.state}</span>
                      {r.validation_warnings.length > 0 && (
                        <div className={styles.warn}>{r.validation_warnings.join('; ')}</div>
                      )}
                    </td>
                    <td className={styles.mono}>
                      {r.kb_entry_id ?? '—'}
                      {r.matched_entry_id && <div className={styles.muted}>← {r.matched_entry_id}</div>}
                      {r.matched_writer && (
                        <div className={r.double_coverage ? styles.warn : styles.muted}>
                          {r.double_coverage ? 'already recorded by ' : 'matched '}{r.matched_writer}
                        </div>
                      )}
                    </td>
                    <td className={styles.muted}>{r.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.h2}>Platform dedup events</h2>
        </div>
        {events.length === 0 ? (
          <div className={styles.empty}>No platform events mirrored yet — run a sync.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table} data-testid="events-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Verdict</th>
                  <th>Executed</th>
                  <th>Entries</th>
                  <th>Consumer</th>
                  <th>Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.event_id}>
                    <td className={styles.muted}>{fmt(e.event_created_at)}</td>
                    <td>
                      <span className={styles.chip}>{e.action}</span>
                      <div className={styles.muted}>
                        {e.source ?? ''}{e.max_similarity_score != null ? ` · sim ${e.max_similarity_score.toFixed(2)}` : ''}
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.chip} ${e.execution_status === 'failed' ? styles.off : ''}`}>
                        {e.execution_status ?? '—'}
                      </span>
                      {e.execution_error && <div className={styles.warn}>{e.execution_error.slice(0, 160)}</div>}
                    </td>
                    <td className={styles.mono}>
                      {e.target_entry_id && <div>target {e.target_entry_id.slice(0, 8)}</div>}
                      {e.result_entry_id && <div>result {e.result_entry_id.slice(0, 8)}</div>}
                      {e.deleted_entry_ids.length > 0 && <div>deleted {e.deleted_entry_ids.length}</div>}
                    </td>
                    <td>
                      <span className={styles.chip}>{e.consume_state}</span>
                      {(e.citations_rewritten > 0 || e.ingestion_rows_repointed > 0) && (
                        <div className={styles.muted}>
                          {e.citations_rewritten} cit · {e.ingestion_rows_repointed} rows
                        </div>
                      )}
                      {e.consume_error && <div className={styles.warn}>{e.consume_error}</div>}
                    </td>
                    <td className={styles.muted}>{e.reasoning ?? e.content_preview ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
